import { createRef, Component } from 'react';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

import { SILVER_PLAN, GOLD_PLAN } from '../../../data/subscriptionPlans';
import withComponentName from '../../../decorators/withComponentName';
import withPriceCurrency from '../PriceCurrencyProvider/withPriceCurrency';
import { getUserToken } from '../../../helpers/getUserToken';

import PiwikContext from '../../../services/piwikContext';

import PlanItem from '../PlanItem';
import Preloader from '../../Preloader';
import PlanItemMobile from '../PlanItem/PlanItemMobile';
import Slider from '../../Shared/ReactSlick';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

class PricingPlans extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      slideNum: 1,
      openedPlanInfo: false,
      hidePrevArrow: false,
      hideNextArrow: false,
      activePlane: 'silver_plan',
      activePlaneType: '1month',
      isAddOnActive: false,
      addOnType: 'personal',
    };
    this.slider = createRef();
    this.backBtn = createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { loading, width } = this.props;
    if (!loading && loading !== prevProps.loading && width <= 767) {
      this.setActiveMainSlide();
    }
  }

  // method to open plan card on mobile devices
  openMobilePlanCard(openedPlanInfo) {
    this.setState({
      openedPlanInfo,
    }, () => {
      if (!openedPlanInfo) return;

      const { width } = this.props;
      const headerHeight = width > 767 ? 80 : 50;
      const y = this.backBtn.current.getBoundingClientRect().top - headerHeight - 154;

      window.scroll({
        top: y,
        behavior: 'smooth',
      });
    });
  }

  // hide/show slider arrows depending on active slide
  trackSlideIndex(oldIndex, newIndex) {
    // workaround to prevent delay in slider animation
    setTimeout(() => {
      switch (newIndex) {
        case 2:
          this.setState({
            hideNextArrow: true,
            hidePrevArrow: false,
          });
          break;
        case 0:
          this.setState({
            hideNextArrow: false,
            hidePrevArrow: true,
          });
          break;
        case 1:
          this.setState({
            hideNextArrow: false,
            hidePrevArrow: false,
          });
          break;
        default:
          this.setState({
            hideNextArrow: false,
            hidePrevArrow: false,
          });
          break;
      }
    }, 400);
  }

  onSubscribe(priceInfo) {
    const { piwikEnabled, currency } = this.props;
    const { isAddOnActive, addOnType } = this.state;

    const token = getUserToken();

    if (token) {
      let {
        id,
        plan_key: planKey,
        billing_period: billingPeriod,
        billing_period_unit: billingPeriodUnit,
      } = priceInfo;
      const { gbpPlanInfo } = priceInfo;

      const addonSuffix = currency === 'gbp' ? '_gbp' : '';
      if (currency === 'gbp' && gbpPlanInfo) {
        ({
          id,
          plan_key: planKey,
          billing_period: billingPeriod,
          billing_period_unit: billingPeriodUnit,
        } = gbpPlanInfo);
      }

      if (piwikEnabled && planKey === 'silver_plan') {
        _paq.push(['trackEvent', 'Premium pricing', 'Subscribe Silver', `${billingPeriod} ${billingPeriod}`]);
      } else if (piwikEnabled && planKey === 'gold_plan') {
        _paq.push(['trackEvent', 'Premium pricing', 'Subscribe Gold', `${billingPeriodUnit} ${billingPeriodUnit}`]);
      }

      if (id) {
        let params = `purchasable_plan_id=${id}`;
        if (isAddOnActive) {
          params += `&addons=${priceInfo[`uk_addons_${addOnType}${addonSuffix}`]}`;
        }
        window.open(`/users/plan_subscription?${params}`, '_blank');
      }
    } else {
      const { setAuthModalOpened } = this.props;
      setAuthModalOpened(true);
    }
  }

  // set active plan card if user has a plan
  setActiveMainSlide() {
    const { userPlan, premiumPlansData } = this.props;

    // to avoid error during SSR
    if (typeof window === 'undefined') return;

    if (userPlan === 'guest_plan') return this.setState({ loading: false });

    this.setState({
      slideNum: premiumPlansData.findIndex((obj) => obj.current_plan === userPlan),
    }, () => {
      const { slideNum } = this.state;
      this.slider.current.slickGoTo(slideNum);
      setTimeout(() => {
        this.setState({ loading: false });
      }, 300);
    });
  }

  updateParentState(key, value) {
    this.setState({
      [key]: value,
    });
  }

  handlePlanRedirect() {
    const plansSections = document.getElementById('sub_uk_add_pricing');
    if (!plansSections) return;
    const { width, promobarHeight } = this.props;
    const headerHeight = width > 767 ? 80 : 50;
    const topPosition = plansSections?.getBoundingClientRect()?.top;
    const y = window.pageYOffset + topPosition - headerHeight - 60 - promobarHeight;

    window.scroll({
      top: y,
      behavior: 'smooth',
    });
  }

  render() {
    const {
      slideNum,
      openedPlanInfo,
      hidePrevArrow,
      hideNextArrow,
      activePlane,
      activePlaneType,
      isAddOnActive,
      addOnType,
    } = this.state;
    const {
      width, userPlan, loading, premiumPlansData, userPlanType, hasInsightsAccess,
    } = this.props;

    const isUserWithPlan = [SILVER_PLAN, GOLD_PLAN].includes(userPlan);

    const sliderSettings = {
      dots: openedPlanInfo,
      arrows: openedPlanInfo,
      prevArrow: openedPlanInfo && !hidePrevArrow ? (
        <Arrow
          to="prev"
          direction="left"
          cssName="arrow-prev"
        />
      ) : (<></>),
      nextArrow: openedPlanInfo && !hideNextArrow ? (
        <Arrow
          to="next"
          direction="right"
          cssName="arrow-next"
        />
      ) : (<></>),
      adaptiveHeight: false,
      dotsClass: cx('plans-dots'),
      initialSlide: slideNum,
      infinite: false,
      beforeChange: this.trackSlideIndex,
      centerPadding: !openedPlanInfo ? '30px' : undefined,
      centerMode: !openedPlanInfo,
    };

    return (
      <div className={cx('plans_wrapper', { loading })}>
        {loading && <Preloader loading={loading} top="20%" />}
        <div className={cx('pricing_plans_wrapper')}>
          <div className={Styles.pricing_premium_wrapper}>
            {width <= 767 ? (
              <>
                {openedPlanInfo && (
                  <div
                    className={cx('plan-item-back-btn')}
                    onClick={() => this.openMobilePlanCard(false)}
                    ref={this.backBtn}
                  >
                    <button type="button">
                      <FontAwesomeIcon icon={faChevronLeft} />
                      Back
                    </button>
                  </div>
                )}
                <Slider {...sliderSettings} ref={this.slider}>
                  {premiumPlansData.map((planItem) => (
                    <PiwikContext.Consumer key={planItem.text}>
                      {(value) => (
                        <PlanItemMobile
                          {...planItem}
                          key={planItem.current_plan}
                          width={width}
                          userPlan={userPlan}
                          userPlanType={userPlanType}
                          piwikEnabled={value}
                          openMobilePlanCard={this.openMobilePlanCard}
                          isUserWithPlan={isUserWithPlan}
                          openedPlanInfo={openedPlanInfo}
                          activePlane={!isUserWithPlan ? activePlane : ''}
                          activePlaneType={activePlaneType}
                          isAddOnActive={isAddOnActive}
                          addOnType={addOnType}
                          updateParentState={this.updateParentState}
                          onSubscribe={this.onSubscribe}
                          hasInsightsAccess={hasInsightsAccess}
                        />
                      )}
                    </PiwikContext.Consumer>
                  ))}
                </Slider>
              </>
            ) : (
              <>
                {premiumPlansData.map((planItem) => (
                  <PiwikContext.Consumer key={planItem.text}>
                    {(value) => (
                      <PlanItem
                        {...planItem}
                        width={width}
                        userPlan={userPlan}
                        userPlanType={userPlanType}
                        piwikEnabled={value}
                        isUserWithPlan={isUserWithPlan}
                        activePlane={!isUserWithPlan ? activePlane : ''}
                        activePlaneType={activePlaneType}
                        isAddOnActive={isAddOnActive}
                        addOnType={addOnType}
                        updateParentState={this.updateParentState}
                        onSubscribe={this.onSubscribe}
                        hasInsightsAccess={hasInsightsAccess}
                      />
                    )}
                  </PiwikContext.Consumer>
                ))}
              </>
            )}
          </div>
          {isUserWithPlan && (
            <div className={cx('pricing_footer_wrapper')}>
              {hasInsightsAccess && (
                <button
                  type="button"
                  className={cx('pricing_footer_wrapper_btn', 'light')}
                  onClick={this.handlePlanRedirect}
                >
                  Browse Addons
                </button>
              )}
              <a
                className={cx('pricing_footer_wrapper_btn')}
                href="/users/chargebee"
                target="_blank"
              >
                Manage Subscription
              </a>
            </div>
          )}
          <div className={Styles.pricing_note}>
            *Earlier data is available via CSV for a fee.&nbsp;
            <a href="/contact-us">Contact us.</a>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ common, promobar }) => ({
  width: common.width,
  promobarHeight: promobar.height,
});

const Arrow = ({
  direction,
  to,
  cssName,
  onClick,
}) => (
  <span aria-label={to} className={Styles[cssName]} onClick={onClick}>
    <FontAwesomeIcon icon={direction === 'left' ? faChevronLeft : faChevronRight} />
  </span>
);

export default connect(
  mapStateToProps,
  null,
)(
  withComponentName(
    withPriceCurrency(PricingPlans),
  ),
);
